import React from 'react';
import {
    Box,
    Card,
    Grid,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { format } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import {
    IBill,
    IDTransactionLog,
    ILeaseAgreement,
    IProfile,
    IWallet,
} from 'realhaus-sdk';
import { RootStoreContext } from '../../global/storeContext';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { DEFAULT_TRANSACTIONS_LIMIT } from '../../stores/walletStore';
import { useStyles } from './styles';
import { IsSystemWallet } from '../../global/util';
import {
    Activity,
    describeTransaction,
    formatCurrency,
    formatSignedAmount,
    STATUS_TONE_COLORS,
} from '../../utils/transaction';
import { TransactionDetailsDrawer } from './transactionDetails';

const otherPartyId = (trx: IDTransactionLog, userId: string) =>
    trx.senderId === userId ? trx.receiverId : trx.senderId;

export const AccountTransactionsView: React.FC<{
    transactions: IDTransactionLog[];
    canFetchMoreTransaction: boolean;
    getTransactions: () => Promise<void>;
}> = observer(({ transactions, canFetchMoreTransaction, getTransactions }) => {
    const { userStore, leaseStore } = React.useContext(RootStoreContext);
    const [selectedActivity, setSelectedActivity] = React.useState<Activity>();
    const [userProfiles] = React.useState<Map<string, IProfile>>(new Map());
    const [bills] = React.useState<Map<string, IBill>>(new Map());
    const [leases] = React.useState<Map<string, ILeaseAgreement>>(new Map());
    const [activities, setActivities] = React.useState<Activity[]>([]);
    const classes = useStyles();
    const userId = userStore.userId ?? '';

    useEffect(() => {
        if (!transactions) return;

        const requested = new Set<string>();
        const loads: Promise<void>[] = [];
        const loadOnce = (key: string, load: () => Promise<void>) => {
            if (requested.has(key)) return;
            requested.add(key);
            loads.push(load().catch((err) => console.log(err)));
        };

        transactions.forEach((t) => {
            const otherId = otherPartyId(t, userId);
            if (otherId && otherId !== userId && !IsSystemWallet(otherId) && !userProfiles.has(otherId)) {
                loadOnce(`user:${otherId}`, async () => {
                    const p = await userStore.getUserProfile(otherId);
                    if (p) userProfiles.set(otherId, p);
                });
            }

            const { leaseId, billId } = t.meta ?? {};
            if (leaseId && !leases.has(leaseId)) {
                loadOnce(`lease:${leaseId}`, async () => {
                    const lease = await leaseStore.getLeaseAgreementById(leaseId);
                    if (lease) leases.set(leaseId, lease);
                });
            }
            if (leaseId && billId && !bills.has(billId)) {
                loadOnce(`bill:${billId}`, async () => {
                    const b = await leaseStore.getLeaseBill(leaseId, billId);
                    if (b) bills.set(billId, b);
                });
            }
        });

        Promise.allSettled(loads).then(() => {
            setActivities(
                transactions.map((t) => {
                    const { leaseId, billId } = t.meta ?? {};
                    const lease = leaseId ? leases.get(leaseId) : undefined;
                    const bill = billId ? bills.get(billId) : undefined;
                    return {
                        trx: t,
                        lease: lease && leaseId ? { id: leaseId, ...lease } : undefined,
                        bill: bill && billId ? { id: billId, ...bill } : undefined,
                        counterparty: userProfiles.get(otherPartyId(t, userId)),
                    };
                }),
            );
        });
    }, [transactions]);

    return (
        <>
            <Card variant='outlined' elevation={0}>
                <Box className={classes.container} key='wallet-view'>
                    <List>
                        {activities.map((activity) => {
                            const description = describeTransaction(activity, userId);
                            return (
                                <ListItem
                                    button
                                    divider
                                    alignItems='flex-start'
                                    key={activity.trx.id}
                                    onClick={() => setSelectedActivity(activity)}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='body1'
                                                style={{ color: 'black', fontWeight: 500 }}
                                            >
                                                {description.title}
                                            </Typography>
                                        }
                                        secondary={
                                            <>
                                                {description.subtitle && (
                                                    <Typography
                                                        variant='body2'
                                                        color='textSecondary'
                                                        component='span'
                                                        style={{ display: 'block' }}
                                                    >
                                                        {description.subtitle}
                                                    </Typography>
                                                )}
                                                <Typography
                                                    variant='caption'
                                                    color='textSecondary'
                                                    component='span'
                                                >
                                                    {format(activity.trx.timestamp, 'PPp')}
                                                </Typography>
                                            </>
                                        }
                                    />
                                    <Box textAlign='right' ml={2} flexShrink={0}>
                                        <Typography
                                            variant='body1'
                                            style={{
                                                fontWeight: 'bold',
                                                whiteSpace: 'nowrap',
                                                color:
                                                    description.direction === 'in'
                                                        ? STATUS_TONE_COLORS.success
                                                        : undefined,
                                            }}
                                        >
                                            {formatSignedAmount(
                                                description.amount,
                                                description.direction,
                                            )}
                                        </Typography>
                                        <Typography
                                            variant='body2'
                                            style={{ color: STATUS_TONE_COLORS[description.status.tone] }}
                                        >
                                            {description.status.label}
                                        </Typography>
                                    </Box>
                                </ListItem>
                            );
                        })}
                        <ListItem
                            button
                            onClick={getTransactions}
                            disabled={!canFetchMoreTransaction}
                            key='load-more'
                        >
                            <ListItemText inset primary=' Load more transactions...' />
                        </ListItem>
                    </List>
                </Box>
            </Card>

            <TransactionDetailsDrawer
                activity={selectedActivity}
                onClose={() => setSelectedActivity(undefined)}
            />
        </>
    );
});

export const WalletComponent: React.FC = observer(() => {
    const { uiStore, walletStore } = useContext(RootStoreContext);
    const [wallet, setWallet] = useState<IWallet | undefined>();
    const [trxRecords, setTrxRecords] = useState<IDTransactionLog[]>([]);
    const [lastQueryDoc, setLastQueryDoc] =
        React.useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [canFetchMoreTransactions, setCanFetchMoreTransactions] = React.useState<boolean>(false);

    const getTrxActivities = async () => {
        uiStore.showLoading();
        try {
            // fetch transactions
            const activities = await walletStore.getTrxActivities(lastQueryDoc);
            setCanFetchMoreTransactions(
                !!activities && activities.transactions.length === DEFAULT_TRANSACTIONS_LIMIT,
            );

            if (!activities || activities.transactions.length === 0) {
                return;
            }

            setLastQueryDoc(activities.lastDoc);
            const t = activities.transactions;
            setTrxRecords([...trxRecords, ...t]);
        } catch (err) {
            console.error('== Error fetching transaction activities');
            console.error(err);
        } finally {
            uiStore.hideLoading();
        }
    };

    const getWallet = async () => {
        const w = await walletStore.getWallet();
        setWallet(w);
    };
    useEffect(() => {
        const init = async () => {
            uiStore.showLoading();
            try {
                // fetch wallet
                await Promise.allSettled([getWallet(), getTrxActivities()]);
            } finally {
                uiStore.hideLoading();
            }
        };

        init();
    }, []);

    return (
        <>
            <Grid
                container
                justifyContent='space-between'
                style={{ marginTop: '24px', marginBottom: '40px' }}
                spacing={2}
            >
                <Grid item>
                    <div>
                        <Typography variant='body1' color='textSecondary' display='inline'>
                            BALANCE:
                        </Typography>
                        <Typography
                            variant='body1'
                            color='primary'
                            style={{ paddingLeft: '10px', fontWeight: 'bold' }}
                            display='inline'
                        >
                            <strong>{formatCurrency(wallet?.amount ?? 0)}</strong>
                        </Typography>
                    </div>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant='h6'>Recent Transactions</Typography>
                </Grid>
                <Grid item xs={12}>
                    <AccountTransactionsView
                        transactions={trxRecords}
                        canFetchMoreTransaction={canFetchMoreTransactions}
                        getTransactions={getTrxActivities}
                    />
                </Grid>
            </Grid>
        </>
    );
});
